/**
 * Admin → Legal → Documentos (listado)
 * CRUD de documentos y versiones. Las versiones publicadas son INMUTABLES.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Plus, Lock, Send, ThumbsUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  LEGAL_TYPE_LABELS,
  LegalBackLink,
  LegalVersionActions,
  VersionStatusBadge,
} from "@/components/legal/legal-admin-shared";

const LEGAL_DOCUMENT_TYPES = [
  "terms_of_service",
  "privacy_policy",
  "cookie_policy",
  "subscription_policy",
  "byo_third_party_policy",
  "kyc_policy",
  "acceptable_use_policy",
  "ip_takedown_policy",
  "ai_transparency_policy",
  "security_incident_policy",
] as const;
type LegalDocumentType = (typeof LEGAL_DOCUMENT_TYPES)[number];

export const Route = createFileRoute("/admin/ops/legal/documents/")({
  component: LegalDocumentsPage,
  head: () => ({ meta: [{ title: "Documentos Legales — Admin" }] }),
});

interface Doc {
  id: string; type: string; title: string; status: string;
  publishedVersion: { id: string; version: string; publishedAt: string } | null;
  versions: Array<{ id: string; version: string; status: string; createdAt: string }>;
}

function LegalDocumentsPage() {
  const [docs, setDocs] = useState<Doc[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [newVersionOpen, setNewVersionOpen] = useState<string | null>(null);

  const [newType, setNewType] = useState<LegalDocumentType>("terms_of_service");
  const [newTitle, setNewTitle] = useState("");
  const [newVerVersion, setNewVerVersion] = useState("1.0.0");
  const [newVerContent, setNewVerContent] = useState("");
  const [newVerSummary, setNewVerSummary] = useState("");
  const [newVerReacc, setNewVerReacc] = useState(false);

  const reload = () => {
    setLoading(true);
    fetch("/api/admin/legal")
      .then((r) => r.json())
      .then((d: { documents?: Doc[] }) => { setDocs(d.documents ?? []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(reload, []);

  const createDoc = async () => {
    const r = await fetch("/api/admin/legal", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ type: newType, title: newTitle }),
    });
    if (!r.ok) { toast.error("Error creando documento"); return; }
    toast.success("Documento creado");
    setCreateOpen(false);
    reload();
  };

  const createVersion = async (docId: string) => {
    const r = await fetch(`/api/admin/legal/${docId}/versions`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        version: newVerVersion,
        content: newVerContent,
        summary: newVerSummary,
        requiresReacceptance: newVerReacc,
      }),
    });
    if (!r.ok) { toast.error("Error creando versión"); return; }
    toast.success("Versión creada como draft");
    setNewVersionOpen(null);
    reload();
  };

  return (
    <div className="p-6">
      <LegalBackLink />
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-xl font-bold text-foreground">Documentos Legales</h1>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> Nuevo documento
        </Button>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Cargando…</p>}

      <div className="space-y-4" data-testid="legal-documents-list">
        {docs.map((doc) => (
          <Card key={doc.id}>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div>
                <CardTitle className="text-base">
                  <Link
                    to="/admin/ops/legal/documents/$docId"
                    params={{ docId: doc.id }}
                    search={{} as never}
                    className="hover:underline"
                  >
                    {doc.title}
                  </Link>
                </CardTitle>
                <p className="text-xs text-muted-foreground">{doc.type}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant={doc.status === "active" ? "default" : "secondary"}>
                  {doc.status}
                </Badge>
                <Link
                  to="/admin/ops/legal/documents/$docId"
                  params={{ docId: doc.id }}
                  search={{} as never}
                >
                  <Button size="sm" variant="outline">Ver documento</Button>
                </Link>
                <Button
                  size="sm" variant="outline"
                  onClick={() => {
                    setNewVersionOpen(doc.id);
                    setNewVerVersion("1.0.0");
                    setNewVerContent("");
                    setNewVerSummary("");
                    setNewVerReacc(false);
                  }}
                >
                  + Versión
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {doc.versions.map((v) => (
                <div key={v.id} className="flex items-center justify-between border-t border-border px-4 py-2.5">
                  <div className="flex items-center gap-3">
                    {v.status === "published"
                      ? <Lock className="h-3.5 w-3.5 text-green-500" />
                      : v.status === "in_review"
                        ? <Send className="h-3.5 w-3.5 text-amber-500" />
                        : v.status === "approved"
                          ? <ThumbsUp className="h-3.5 w-3.5 text-blue-500" />
                          : <div className="h-3.5 w-3.5 rounded-full border border-border" />
                    }
                    <Link
                      to="/admin/ops/legal/documents/$docId/versions/$versionId"
                      params={{ docId: doc.id, versionId: v.id }}
                      search={{} as never}
                      className="text-sm font-medium hover:underline"
                    >
                      v{v.version}
                    </Link>
                    <VersionStatusBadge status={v.status} />
                    <span className="text-xs text-muted-foreground">
                      {new Date(v.createdAt).toLocaleDateString("es-AR")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to="/admin/ops/legal/documents/$docId/versions/$versionId"
                      params={{ docId: doc.id, versionId: v.id }}
                      search={{} as never}
                    >
                      <Button size="sm" variant="ghost">Ver versión</Button>
                    </Link>
                    <LegalVersionActions docId={doc.id} version={v} onDone={reload} />
                  </div>
                </div>
              ))}
              {doc.versions.length === 0 && (
                <p className="px-4 py-3 text-xs text-muted-foreground">Sin versiones</p>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nuevo documento legal</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <select
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                value={newType}
                onChange={(e) => setNewType(e.target.value as LegalDocumentType)}
              >
                {LEGAL_DOCUMENT_TYPES.map((t) => (
                  <option key={t} value={t}>{LEGAL_TYPE_LABELS[t]}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Título</Label>
              <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Términos y Condiciones" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={() => void createDoc()} disabled={!newTitle || !newType}>Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!newVersionOpen} onOpenChange={(o) => { if (!o) setNewVersionOpen(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Nueva versión (draft)</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Número de versión</Label>
                <Input value={newVerVersion} onChange={(e) => setNewVerVersion(e.target.value)} placeholder="1.0.0" />
              </div>
              <div className="flex items-end gap-2 pb-0.5">
                <input
                  type="checkbox"
                  id="reacceptance"
                  checked={newVerReacc}
                  onChange={(e) => setNewVerReacc(e.target.checked)}
                  className="h-4 w-4"
                />
                <Label htmlFor="reacceptance" className="cursor-pointer text-sm">
                  Requiere reacceptance
                </Label>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Resumen de cambios</Label>
              <Input value={newVerSummary} onChange={(e) => setNewVerSummary(e.target.value)} placeholder="Actualización de política de…" />
            </div>
            <div className="space-y-1.5">
              <Label>Contenido (Markdown)</Label>
              <Textarea
                value={newVerContent}
                onChange={(e) => setNewVerContent(e.target.value)}
                className="h-64 font-mono text-xs"
                placeholder="# Título del documento&#10;&#10;## Sección 1…"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewVersionOpen(null)}>Cancelar</Button>
            <Button
              onClick={() => newVersionOpen && void createVersion(newVersionOpen)}
              disabled={!newVerVersion || !newVerContent}
            >
              Crear draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
